import { Typography, Button, Paper } from "@material-ui/core";
import React, { useState } from "react";
import { ReactComponent as Info } from "icons/Info.svg";
import { impactDecisionStyles } from "./ImpactDecisionStyles";
import { observer } from "mobx-react";
import { ImpactDecisionProps } from "./ImpactDecisionProps";
import { useStore } from "store/StoreConfigs";
import clsx from "clsx";
import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";
import ExcursionsModal from "components/ExcursionsModal/ExcursionsModal";
import AuthModal from "components/AuthModal/AuthModal";
import { useOktaAuth } from "@okta/okta-react";
import { AuthModel, BatchStatusModel } from "api/thorRequestModels/Events/BatchStatusRequest";
import { pluralize } from "helpers/pluralize";
import LynxSelect from "components/LynxSelect/LynxSelect";
import { AssessmentStatus } from "models/thorEventModels/BatchImpactAssessment";
import ImpactDecisionRecommendation from "components/ImpactDecisionRecommendation/ImpactDecisionRecommendation";

export default observer(function ImpactDecision({
    batchId,
    decision,
    isReturnedToStock,
    quantity,
    assessmentStatusId,
}: ImpactDecisionProps) {
    const classes = impactDecisionStyles();
    const { thorEventViewStore, identityStore } = useStore();
    const [modalType, setModalType] = useState<"apply" | "discard">("apply");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { oktaAuth } = useOktaAuth();
    const [isShowReAuthenticate, setIsShowReAuthenticate] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState("");
    const [numberOfRetries, setNumberOfRetries] = React.useState(2);

    const recommendationDecision = thorEventViewStore.eventSummary.batchSummaries.find(
        (decision) => decision.batchId === batchId
    )!.impactDecision.isApprovedRecommendation;

    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [batchStatus, setBatchStatus] = useState({});

    const handleReturnToStockDecision = (e: any) => {
        thorEventViewStore.returnToStock({
            batchId: batchId,
            decision: e.target.value === "yes",
            eventId: thorEventViewStore.eventDetails.eventId,
            customerId: identityStore.currentCustomerId,
        });
    };
    const handleClose = () => {
        setTooltipOpen(false);
    };

    const handleOpen = () => {
        setTooltipOpen(true);
    };

    const onAuthModalClose = () => {
        setIsShowReAuthenticate(false);
        setErrorMessage("");
        setNumberOfRetries(2);
    };

    const handleBatchStatusChangeResponse = async (statusCode: number) => {
        if (statusCode === 401) {
            setNumberOfRetries(numberOfRetries - 1);
            setErrorMessage("Incorrect login or password. Number attempts left: " + numberOfRetries);
        } else if (statusCode === 204) {
            setNumberOfRetries(2);
            thorEventViewStore.setSelectedBatch(0);
            return;
        } else {
            setErrorMessage("Something went wrong. Please, try again");
        }

        if (numberOfRetries <= 0) {
            thorEventViewStore.setSelectedBatch(0);
            setNumberOfRetries(2);
            await oktaAuth.signOut();
        }
    };

    const submitBatchDecision = (authModel: AuthModel) => {
        const status = batchStatus as BatchStatusModel;
        thorEventViewStore.setBatchStatus({
            customerId: identityStore.currentCustomerId,
            approvalMessage: status.approvalMessage,
            batchId: status.batchId,
            eventId: status.eventId,
            isApproved: status.isApproved,
            authModel,
            callback: handleBatchStatusChangeResponse,
        });
    };

    const onBatchStatusSet = (batchStatus: BatchStatusModel) => {
        setBatchStatus(batchStatus);
        setIsModalOpen(false);
        setIsShowReAuthenticate(true);
    };

    return (
        <div className={classes.main}>
            <div style={{ position: "absolute" }}>
                <Typography variant="h4" className={classes.title}>
                    Impact Decision
                    <Info className={classes.icon} onMouseEnter={handleOpen} onMouseLeave={handleClose} />
                </Typography>
                <Paper
                    elevation={0}
                    className={clsx(classes.tooltip, {
                        [classes.hide]: !tooltipOpen,
                    })}
                    onMouseEnter={handleOpen}
                    onMouseLeave={handleClose}
                >
                    <Typography className={classes.tooltipTitle}>What is an Impact Decision?</Typography>
                    <br />
                    <Typography className={classes.tooltipText}>
                        This section represents the decision recommendation for the batch. The individual responsible
                        for the decision will need to confirm a decision and use either the default justification or
                        create a custom one for the batch.
                    </Typography>
                </Paper>
            </div>
            <div style={{ marginTop: "70px" }}>
                <ImpactDecisionRecommendation
                    recommendationDecision={recommendationDecision}
                    assessmentStatusId={assessmentStatusId}
                    batchId={batchId}
                    decision={decision}
                />

                {thorEventViewStore.eventDetails.qaUserId === identityStore.userInfo?.email &&
                    (assessmentStatusId === AssessmentStatus.PendingDecision ||
                        assessmentStatusId === AssessmentStatus.ReAssessing) && (
                        <div className={classes.buttonsContainer}>
                            {thorEventViewStore.IsReturnedToStockSet ? (
                                <div className={classes.smallIndicator}>
                                    <LoadingIndicator />
                                </div>
                            ) : (
                                <div className={classes.selectContainer}>
                                    <LynxSelect
                                        label={
                                            pluralize("Product", quantity) +
                                            " " +
                                            pluralize("is", quantity) +
                                            (isReturnedToStock ? " " : " not ") +
                                            "going to stock"
                                        }
                                        margin={"0px 0px 0px 0px"}
                                        isMultiple={false}
                                        value={isReturnedToStock ? "yes" : "no"}
                                        onChange={handleReturnToStockDecision}
                                        options={[
                                            {
                                                itemName: "Yes",
                                                itemValue: "yes",
                                            },
                                            {
                                                itemName: "No",
                                                itemValue: "no",
                                            },
                                        ]}
                                    />
                                </div>
                            )}
                            <Button
                                onClick={() => {
                                    setModalType("apply");
                                    setIsModalOpen(true);
                                }}
                                className={clsx(classes.button, classes.approveButton, {
                                    [classes.approveRecommendation]: recommendationDecision,
                                })}
                            >
                                Excursion is supported
                            </Button>
                            <Button
                                onClick={() => {
                                    setModalType("discard");
                                    setIsModalOpen(true);
                                }}
                                className={clsx(classes.button, classes.discardButton, {
                                    [classes.discardRecommendation]: recommendationDecision === false,
                                })}
                            >
                                Excursion is not supported
                            </Button>
                        </div>
                    )}
                {thorEventViewStore.isEventSummaryLoaded ? null : (
                    <ExcursionsModal
                        isOpen={isModalOpen}
                        batchId={batchId}
                        type={modalType}
                        onClose={() => {
                            setIsModalOpen(false);
                        }}
                        onConfirm={(batchStatus: BatchStatusModel) => {
                            onBatchStatusSet(batchStatus);
                        }}
                    />
                )}
                <AuthModal
                    isOpen={isShowReAuthenticate}
                    onCancel={onAuthModalClose}
                    onClose={onAuthModalClose}
                    onSubmit={(authModel: AuthModel) => {
                        submitBatchDecision(authModel);
                    }}
                    errorMessage={errorMessage}
                />
            </div>
        </div>
    );
});
